import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Send, X, Loader2 } from 'lucide-react';
import { api } from '@/api/client';
import type { ApiResponse } from '@/api/types';

interface WaveModalProps {
  /** User receiving the wave. */
  toUserId: string;
  /** Display name for the copy ("Let {firstName} know..."). */
  toUserName?: string | null;
  /** Whether the modal is currently visible. */
  open: boolean;
  /** Called to request closing the modal (X, Cancel, backdrop). */
  onClose: () => void;
  /** Called after a wave is sent successfully. */
  onSent?: () => void;
}

/**
 * Shared "Send a Wave" modal used from both the Matches list and MatchDetail.
 * Owns the message state and the send mutation; parent only controls visibility.
 */
export default function WaveModal({
  toUserId,
  toUserName,
  open,
  onClose,
  onSent,
}: WaveModalProps) {
  const [message, setMessage] = useState('');
  const queryClient = useQueryClient();

  const sendWave = useMutation({
    mutationFn: async () => {
      return api.post<ApiResponse<{ waveId: string }>>(
        '/identity-board/discovery/waves',
        {
          toUserId,
          message: message || undefined,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['match-detail', toUserId] });
      queryClient.invalidateQueries({ queryKey: ['matches'] });
      queryClient.invalidateQueries({ queryKey: ['waves'] });
      setMessage('');
      onSent?.();
      onClose();
    },
  });

  if (!open) return null;

  const firstName = toUserName?.split(' ')[0] ?? 'them';

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Send a Wave</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-gray-600 mb-4">
          Let {firstName} know you're interested in connecting!
        </p>

        <div className="mb-4">
          <label htmlFor="waveMessage" className="label">
            Add a message (optional)
          </label>
          <textarea
            id="waveMessage"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Hi! I noticed we both..."
            className="input min-h-[100px] resize-none"
            maxLength={200}
          />
          <p className="text-xs text-gray-500 text-right mt-1">
            {message.length}/200
          </p>
        </div>

        <div className="flex gap-3">
          <button onClick={onClose} className="btn-ghost flex-1">
            Cancel
          </button>
          <button
            onClick={() => sendWave.mutate()}
            disabled={sendWave.isPending}
            className="btn-primary flex-1 flex items-center justify-center gap-2"
          >
            {sendWave.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Send className="w-4 h-4" />
                Send Wave
              </>
            )}
          </button>
        </div>

        {sendWave.isError && (
          <p className="text-sm text-red-600 mt-3 text-center">
            Failed to send wave. Please try again.
          </p>
        )}
      </div>
    </div>
  );
}
